import { TemperatureUnit } from '@constants/utils'
import type { DepthUnit, DistanceUnit, SpeedUnit } from '@shared_types/unit_types'
import { isNumber } from 'lodash'

interface ConvertOptions {
  precision?: number
  round?: boolean
  showUnit?: boolean
}

let numberFormatLocale: string = 'en'

export function setNumberFormatLocale (locale: string) {
  numberFormatLocale = locale
}

function isInvalid (val: number) {
  return Number.isNaN(Number(val)) || !Number.isFinite(val)
}

export function formatNumberWithPrecision (value: number, unit: string, options?: ConvertOptions) {
  const precision = isNumber(options?.precision) ? (options?.precision as number) : 0
  const formatOptions = { maximumFractionDigits: Math.min(precision, 3), minimumFractionDigits: Math.min(precision, 3) }
  const str = value.toLocaleString(numberFormatLocale, formatOptions)
  return options?.showUnit ? `${str} ${unit}` : str
}

export function getDistanceUnit () {
  return (localStorage.getItem('distanceUnit') || 'nmi') as DistanceUnit
}

export function getSpeedUnit () {
  return (localStorage.getItem('speedUnit') || 'kn') as SpeedUnit
}

export function getTempUnit () {
  return (localStorage.getItem('tempUnit')) as TemperatureUnit
}

export function convertTemperature (celsius: number, toUnit: TemperatureUnit, options?: ConvertOptions) {
  if (isInvalid(celsius)) {
    return '-'
  }
  const v = toUnit === TemperatureUnit.CELSIUS ? celsius : celsius * 1.8 + 32
  return formatNumberWithPrecision(v, toUnit, options)
}

export function convertSpeed (knots: number, toUnit: SpeedUnit, options?: ConvertOptions) {
  if (isInvalid(knots)) {
    return '-'
  }
  const v = toUnit === 'kph' ? knots * 1.852 : toUnit === 'mph' ? knots * 1.15078 : knots
  return formatNumberWithPrecision(v, toUnit, options)
}

export function convertDepth (meters: number, toUnit: DepthUnit, options?: ConvertOptions) {
  if (isInvalid(meters)) {
    return '-'
  }
  const v = toUnit === 'ft' ? meters * 3.2808399 : meters
  return formatNumberWithPrecision(v, toUnit, options)
}

export function convertDistance (nauticalMiles: number, toUnit: DistanceUnit, options?: ConvertOptions) {
  if (isInvalid(nauticalMiles)) {
    return '-'
  }
  const v = toUnit === 'km' ? nauticalMiles * 1.852 : toUnit === 'mi' ? nauticalMiles * 1.15078 : nauticalMiles
  return formatNumberWithPrecision(v, toUnit, options)
}

export function convertPressure (hpa: number, toUnit: 'hPa' | 'atm', options?: ConvertOptions) {
  if (isInvalid(hpa)) {
    return '-'
  }
  const v = toUnit === 'hPa' ? hpa : hpa * 0.000987
  return formatNumberWithPrecision(v, toUnit, options)
}

// HOOKS

export function useTemperatureConverter (unit: TemperatureUnit, options?: ConvertOptions) {
  return (celsius: number) => convertTemperature(celsius, unit, options)
}

export function useSpeedConverter (unit: SpeedUnit, options?: ConvertOptions) {
  return (knots: number) => convertSpeed(knots, unit, options)
}

export function useDistanceConverter (unit: DistanceUnit, options?: ConvertOptions) {
  return (nmi: number) => convertDistance(nmi, unit, options)
}
